import React from 'react';
import { NiceUrlPageContext } from '../app.types/pages';
import { createPageContainer } from '../app.state/createPageContainer';
import { createSeoPage } from '../app.base/pages';
import { PageInfoModel } from '@whop/niceurls/types';
import { NotFoundPage } from '../tsx.app.global/NotFoundPage';
import { usePagesContext } from '../app.context/pagesContext';

type InitialProps = {
  pageInfo?: PageInfoModel;
  pageProps?: object;
  redirectInfo?: { redirectUrl: string };
};

function NiceUrlPage(props: InitialProps) {
  // @note(frameworkPageProps): passing injected props along to the component's props
  // - @review is it usefull, necessary?
  const { redirectInfo, pageInfo, pageProps, ...frameworkPageProps } = props;

  const { selectNiceUrlComponent } = usePagesContext();

  React.useEffect(() => {
    if (redirectInfo) {
      const { redirectUrl } = redirectInfo;
      // Note: in edge-case of API bug (redirectUrl = '') it would keep redirecting in a loop
      if (!redirectUrl) {
        return;
      }
      // @review: replace with router? do it already in getInitialProps for SSR?
      // see FeatUrlInternalOrExternal: this currently handles even external links,
      // we can branch and redirect with router for internal only.
      window.location.replace(redirectInfo.redirectUrl);
    }
  }, [redirectInfo]);

  if (redirectInfo) {
    // Do not render anything before redirecting
    return null;
  }

  if (!pageInfo) {
    return <NotFoundPage />;
  }

  const PageComponent = selectNiceUrlComponent(pageInfo.model);
  if (!PageComponent) {
    return <NotFoundPage />;
  }
  return <PageComponent {...pageProps} {...frameworkPageProps} />;
}

export default createSeoPage<InitialProps>(NiceUrlPage, {
  getInitialProps: async (ctx: NiceUrlPageContext) => {
    const { appContainer } = ctx;
    const { universal, i18n } = appContainer.context;
    const pathname = universal.getUrlPath();
    const search = universal.getUrlSearch();
    const url = pathname;

    const pageContainer = await appContainer.resolveOnce(createPageContainer);
    const result = await pageContainer.getNiceUrlPageInfo({
      pathname,
      search,
      ctx: {
        appContainer,
        pageContainer,
      },
    });

    if (result.status === 'ok') {
      const { pageInfo, pageProps } = result;
      const meta = {
        title: pageInfo.title,
        description: pageInfo.description,
        keywords: pageInfo.keywords,
        key: url,
        canonicalLink: pageInfo.canonicalLink,
      };
      return {
        pageInfo,
        pageProps,
        __meta: meta,
      };
    }

    const metaNotFound = {
      title: i18n.t`404 | Not found`,
      description: '',
      canonicalLink: '',
      key: url,
      keywords: '',
    };

    if (result.status === 'redirect') {
      const { redirectInfo } = result;
      universal.performRedirect({ to: result.redirectInfo.redirectUrl });
      return {
        redirectInfo,
        __meta: metaNotFound,
      };
    }

    return {
      __meta: metaNotFound,
    };
  },
});
